import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";

const ROW_KEY = "docs-row-size-v4"; // v4: default 114 -> 54
const PREVIEW_KEY = "docs-preview-size-v2"; // v2: viewport-derived default, max 960
export const ROW_MIN = 22;
export const ROW_MAX = 130;
export const ROW_STEP = 16;
export const ROW_DEFAULT = 54;
export const PREVIEW_MIN = 160;
export const PREVIEW_MAX = 960;
export const PREVIEW_STEP = 80;

// Default: whole A4 first page (h = 1.414 w) fits in ~75% of the window height.
export function previewDefault() {
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const w = Math.round((0.75 * vh) / 1.414 / PREVIEW_STEP) * PREVIEW_STEP;
  return Math.min(PREVIEW_MAX, Math.max(PREVIEW_MIN, w));
}

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

function read(key, def, lo, hi) {
  try {
    const v = parseInt(window.localStorage.getItem(key), 10);
    return Number.isFinite(v) ? clamp(v, lo, hi) : def;
  } catch {
    return def;
  }
}

const state = {
  rowSize: read(ROW_KEY, ROW_DEFAULT, ROW_MIN, ROW_MAX),
  previewSize: read(PREVIEW_KEY, null, PREVIEW_MIN, PREVIEW_MAX), // null = not chosen -> live default
};
const subs = new Set();
const emit = () => subs.forEach((fn) => fn());

function write(key, field, value, lo, hi) {
  const v = clamp(value, lo, hi);
  state[field] = v;
  try {
    window.localStorage.setItem(key, String(v));
  } catch {
    /* storage unavailable: keep in memory */
  }
  emit();
}

const setRowSize = (v) => write(ROW_KEY, "rowSize", v, ROW_MIN, ROW_MAX);
const setPreviewSize = (v) => write(PREVIEW_KEY, "previewSize", v, PREVIEW_MIN, PREVIEW_MAX);

// Re-read from storage (other tabs, tests).
export function _reloadDocSizes() {
  state.rowSize = read(ROW_KEY, ROW_DEFAULT, ROW_MIN, ROW_MAX);
  state.previewSize = read(PREVIEW_KEY, null, PREVIEW_MIN, PREVIEW_MAX);
  emit();
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === ROW_KEY || e.key === PREVIEW_KEY) _reloadDocSizes();
  });
}

export function useDocSizes() {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    subs.add(fn);
    return () => subs.delete(fn);
  }, []);
  return { rowSize: state.rowSize, setRowSize, previewSize: state.previewSize ?? previewDefault(), setPreviewSize };
}

const BTN =
  "h-7 w-7 shrink-0 inline-flex items-center justify-center rounded border border-border bg-background hover:bg-muted disabled:opacity-40";

export function RowSizeControl({ className = "" }) {
  const { rowSize, setRowSize: set } = useDocSizes();
  return (
    <div className={`inline-flex shrink-0 items-center gap-1 ${className}`}>
      <button
        type="button"
        className={BTN}
        aria-label="Smaller rows"
        disabled={rowSize <= ROW_MIN}
        onClick={() => set(rowSize - ROW_STEP)}
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        className={BTN}
        aria-label="Larger rows"
        disabled={rowSize >= ROW_MAX}
        onClick={() => set(rowSize + ROW_STEP)}
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
